'use client';

import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useIsClient } from '@/hooks/useIsClient';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  variant?: 'default' | 'brand-teal';
  className?: string;
  ariaLabelledBy?: string;
  ariaLabel?: string;
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-full mx-4',
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Modal({ 
  open, 
  onClose, 
  children, 
  size = 'md', 
  variant = 'default',
  className = '',
  ariaLabelledBy,
  ariaLabel,
}: ModalProps) {
  const mounted = useIsClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onCloseRef.current = onClose; });

  const trapFocus = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;

    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter(el => el.offsetParent !== null);

    if (focusable.length === 0) {
      e.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeIsInside = dialogRef.current.contains(document.activeElement);

    if (!activeIsInside) {
      e.preventDefault();
      first.focus();
      return;
    }

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCloseRef.current();
      trapFocus(e);
    }

    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';

    const inertElements: Element[] = [];
    const modalEl = dialogRef.current;
    if (modalEl) {
      for (const child of Array.from(document.body.children)) {
        if (child !== modalEl && !child.hasAttribute('inert')) {
          child.setAttribute('inert', '');
          inertElements.push(child);
        }
      }
    }

    const timer = setTimeout(() => {
      if (!dialogRef.current) return;
      const firstFocusable = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      if (firstFocusable) {
        firstFocusable.focus();
      } else {
        dialogRef.current.focus();
      }
    }, 50);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
      inertElements.forEach(el => el.removeAttribute('inert'));
      previousFocusRef.current?.focus();
    };
  }, [open, trapFocus]);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledBy}
      aria-label={!ariaLabelledBy ? (ariaLabel ?? 'Dialog') : undefined}
      ref={dialogRef}
      tabIndex={-1}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => onCloseRef.current()}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className={`relative z-10 w-full ${sizeClasses[size]} rounded-2xl shadow-2xl transition-all duration-300 ${
          variant === 'brand-teal' ? 'bg-brand-teal text-white' : 'bg-white text-gray-900'
        } ${className}`}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={() => onCloseRef.current()}
          aria-label="Luk"
          className={`absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 rounded-full p-2 transition-all z-20 shadow-lg focus:outline-none focus:ring-3 focus:ring-brand-dark ${
            variant === 'brand-teal'
              ? 'bg-white text-brand-dark hover:scale-110 active:scale-95'
              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-gray-600'
          }`}
        >
          <svg className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="overflow-y-auto max-h-[90vh] rounded-2xl">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
